import { useContext, useState } from 'react';
import { Languages } from 'lucide-react';
import { Button, type LocalizedTranslateProps, Modal, useToast } from '@lp/portal-ui';
import { AI_TRANSLATE_MAX_CHARS, AI_TRANSLATE_MAX_FIELDS, type AiTranslateResult, LANG_META, translateLoad } from '@lp/shared';
import { api } from '../api.ts';
import { errorMessage } from '../lib/errors.ts';
import { SessionContext } from '../session.tsx';
import { Segmented } from './ui.tsx';

/** Has content: text, or for rich text also a picture alone. */
const filled = (v: string | undefined, html?: boolean) => !!v && (html ? /<img\b/i.test(v) || v.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '' : v.trim() !== '');

/**
 * 「AI 翻譯其他語言」 under every form's language tabs (aiTranslate add-on): fills the brand's other languages from the
 * tab being edited, all fields of the form in one call; by default only the empty ones. Given to portal-ui's
 * LocalizedTranslate in main.tsx, so it reads the session itself and hides when the brand lacks the add-on.
 */
export function AiTranslateButton({ lang, languages, fields, fill }: LocalizedTranslateProps) {
  const session = useContext(SessionContext);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!session || session.readOnly || !session.hasModule('aiTranslate')) return null;

  const others = languages.filter((l) => l !== lang);
  const sources = open ? fields().filter((f) => filled(f.value[lang], f.html)) : [];
  const plan = sources.map((f) => ({ f, to: others.filter((l) => overwrite || !filled(f.value[l], f.html)) })).filter((p) => p.to.length);
  const tooLong = plan.length > AI_TRANSLATE_MAX_FIELDS || translateLoad(plan.map((p) => ({ text: p.f.value[lang]!, to: p.to }))) > AI_TRANSLATE_MAX_CHARS;

  async function translate() {
    setBusy(true);
    try {
      const res = await api.post<AiTranslateResult>('/ai/translate', {
        from: lang,
        fields: plan.map(({ f, to }) => ({ label: f.label.slice(0, 60) || undefined, text: f.value[lang]!.trim(), html: f.html, maxLength: f.maxLength, to })),
      });
      fill(Object.fromEntries(plan.map(({ f }, i) => [f.id, res.fields[i] ?? {}])));
      const asked = plan.reduce((n, p) => n + p.to.length, 0);
      const done = res.fields.reduce((n, t) => n + Object.keys(t).length, 0);
      if (done < asked) toast.error(`已翻譯 ${done} / ${asked} 項，其餘未能翻譯，請人手填寫或稍後再試。`);
      else toast.success(`已翻譯 ${plan.length} 個欄位，請切換語言分頁檢查譯文。`);
      setOpen(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="-mt-2 mb-4 flex justify-end">
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline">
        <Languages className="size-4" aria-hidden />
        AI 翻譯其他語言
      </button>
      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title={
          <span className="inline-flex items-center gap-2">
            <Languages className="size-5 text-brand-700" aria-hidden />
            AI 翻譯
          </span>
        }
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button loading={busy} disabled={!plan.length || tooLong} onClick={() => void translate()}>
              翻譯
            </Button>
          </>
        }
      >
        <div className="space-y-4 text-[15px] text-ink-soft">
          <p>
            由<b className="font-medium text-ink">{LANG_META[lang].label}</b>翻譯至{others.map((l) => LANG_META[l].label).join('、')}，表單內的欄位會一次過翻譯。
          </p>
          <Segmented
            label="翻譯範圍"
            value={overwrite ? 'all' : 'empty'}
            options={[
              { value: 'empty', label: '只填寫空白欄位' },
              { value: 'all', label: '覆蓋已有內容' },
            ]}
            onChange={(v) => setOverwrite(v === 'all')}
          />
          <p className="text-sm">
            {!sources.length
              ? `目前的語言（${LANG_META[lang].label}）未有內容可翻譯，請先填寫。`
              : !plan.length
                ? '其他語言已全部填寫。如要重新翻譯，請選擇「覆蓋已有內容」。'
                : tooLong
                  ? plan.length > AI_TRANSLATE_MAX_FIELDS
                    ? `欄位較多：一次最多可翻譯 ${AI_TRANSLATE_MAX_FIELDS} 個欄位，這次有 ${plan.length} 個。請選擇「只填寫空白欄位」，或先人手翻譯部分欄位。`
                    : `內容較長：一次最多可翻譯約 ${AI_TRANSLATE_MAX_CHARS.toLocaleString()} 字（按目標語言數目計算）。請選擇「只填寫空白欄位」，或先人手翻譯部分欄位。`
                  : `將翻譯 ${plan.length} 個欄位。`}
          </p>
          <p className="text-xs text-muted">AI 譯文為草稿，請切換語言分頁檢查後才儲存或發佈。</p>
        </div>
      </Modal>
    </div>
  );
}
